import React from "react";
import { Button } from "../../../../../ui/components/Button";
import { Badge } from "../../../../../ui/components/Badge";
import { Table } from "../../../../../ui/components/Table";
import { ToggleGroup } from "../../../../../ui/components/ToggleGroup";
import { DropdownMenu } from "../../../../../ui/components/DropdownMenu";
import {
  FeatherChevronDown,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherCheckCircle,
  FeatherAlertCircle,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { truncateText } from "../../../../../shared/utils/formatters";
import { useRecentBankTransactions } from "../../hooks/useRecentBankTransactions";
import type { RecentBankTransactionItemEntity } from "../../../domain/entities/RecentBankTransactionsEntity";
import type { RecentBankTransactionsPeriod } from "../../../domain/repositories/CashAndBankRepository";
import { formatIndianAmount } from "../../utils/formatPlAmount";
import { getRecentActivityDateRangeSubtext } from "../../utils/recentRevenueActivityDateRange";

export type TransactionPeriod = "last7d" | "last30d" | "last90d" | "custom";
export type TransactionFilter = "all" | "inflows" | "outflows" | "needs_review";

function getPeriodLabel(
  period: RecentBankTransactionsPeriod,
  customStartDate?: string,
  customEndDate?: string,
): string {
  switch (period) {
    case "last7d":
      return "Last 7 Days";
    case "last30d":
      return "Last 30 Days";
    case "last90d":
      return "Last 90 Days";
    case "custom":
      if (customStartDate && customEndDate) {
        try {
          const start = new Date(customStartDate);
          const end = new Date(customEndDate);
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
          }
        } catch {
          // fall through
        }
      }
      return "Custom Range";
    default:
      return "Last 7 Days";
  }
}

function formatAmount(row: { amount: number; isCredit: boolean }): string {
  const signed = row.isCredit ? Math.abs(row.amount) : -Math.abs(row.amount);
  return formatIndianAmount(signed);
}

function statusVariant(
  row: RecentBankTransactionItemEntity,
): "success" | "warning" | "error" {
  if (row.reviewStatus === "pending") return "warning";
  if (row.reviewStatus === "rejected") return "error";
  return "success";
}

export function RecentBankTransactionsSection() {
  const {
    period,
    onPeriodChange,
    showCustomRange,
    customStartDate,
    customEndDate,
    onCustomStartDateChange,
    onCustomEndDateChange,
    onCustomApply,
    onCustomRangeCancel,
    filter,
    onFilterChange,
    rows,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    onPageChange,
  } = useRecentBankTransactions();

  const dateRangeSubtext = getRecentActivityDateRangeSubtext(
    period ?? "last7d",
    customStartDate,
    customEndDate,
  );

  const handleCustomApply = () => {
    if (customStartDate && customEndDate) {
      onCustomApply(customStartDate, customEndDate);
    }
  };

  return (
    <div className="flex w-full flex-col items-start gap-6 rounded-lg bg-default-background px-8 py-8 shadow-md">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Recent Bank Transactions
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {dateRangeSubtext}
          </span>
        </div>
        {showCustomRange ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => onCustomStartDateChange(e.target.value)}
              className="rounded-md border border-neutral-border bg-default-background px-3 py-1.5 text-sm text-default-font"
              aria-label="Start date"
            />
            <span className="text-body font-body text-subtext-color">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => onCustomEndDateChange(e.target.value)}
              className="rounded-md border border-neutral-border bg-default-background px-3 py-1.5 text-sm text-default-font"
              aria-label="End date"
            />
            <Button
              variant="brand-primary"
              size="small"
              onClick={handleCustomApply}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
            <Button
              variant="neutral-secondary"
              size="small"
              onClick={onCustomRangeCancel}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <Button
                variant="neutral-secondary"
                iconRight={<FeatherChevronDown />}
              >
                {getPeriodLabel(period, customStartDate, customEndDate)}
              </Button>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="end"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("last7d")}
                  >
                    Last 7 days
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("last30d")}
                  >
                    Last 30 days
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("last90d")}
                  >
                    Last 90 days
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem
                    icon={null}
                    onClick={() => onPeriodChange("custom")}
                  >
                    Custom Range...
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
        )}
      </div>
      <div className="flex w-full items-center gap-2">
        <ToggleGroup
          value={filter}
          onValueChange={(value: string) => {
            if (
              value === "all" ||
              value === "inflows" ||
              value === "outflows" ||
              value === "needs_review"
            ) {
              onFilterChange(value as TransactionFilter);
            }
          }}
        >
          <ToggleGroup.Item icon={null} value="all">
            All
          </ToggleGroup.Item>
          <ToggleGroup.Item icon={null} value="inflows">
            Inflows
          </ToggleGroup.Item>
          <ToggleGroup.Item icon={null} value="outflows">
            Outflows
          </ToggleGroup.Item>
          <ToggleGroup.Item icon={null} value="needs_review">
            Needs Review
          </ToggleGroup.Item>
        </ToggleGroup>
      </div>
      {error ? (
        <div className="text-body font-body text-error-600 py-2">{error}</div>
      ) : null}
      {loading ? (
        <div className="flex w-full flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex w-full items-center gap-4 border-b border-neutral-border px-4 py-3 h-12 animate-pulse rounded bg-neutral-100"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center py-12 text-center">
          <span className="text-body font-body text-subtext-color">
            No transactions found for this period.
          </span>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-start gap-4 flex-shrink-0">
            <Table
              className="w-full table-fixed"
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="w-24 sm:w-28">
                    DATE
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-40 sm:w-48 md:w-56">
                    ACCOUNT
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[12rem] sm:w-[16rem] md:w-[22rem]">
                    DESCRIPTION
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-28 sm:w-36 md:w-44">
                    CATEGORY
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-28 sm:w-32">
                    AMOUNT
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-24 sm:w-28">
                    STATUS
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {rows.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell className="w-24 sm:w-28">
                    <span className="block truncate text-body font-body text-default-font">
                      {row.date}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-40 sm:w-48 md:w-56">
                    <span
                      className="block truncate text-body font-body text-default-font"
                      title={row.accountName}
                    >
                      {truncateText(row.accountName, 22)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[12rem] sm:w-[16rem] md:w-[22rem]">
                    <span
                      className="block truncate text-body font-body text-default-font"
                      title={row.description}
                    >
                      {truncateText(row.description, 22)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-28 sm:w-36 md:w-44">
                    <div className="min-w-0">
                      <Badge
                        variant={row.categoryVariant ?? "neutral"}
                        icon={
                          row.categoryVariant === "warning" ? (
                            <FeatherAlertCircle />
                          ) : undefined
                        }
                      >
                        {truncateText(row.category, 18)}
                      </Badge>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-28 sm:w-32">
                    <span
                      className={`block truncate text-body-bold font-body-bold ${row.isCredit ? "text-success-700" : "text-error-700"}`}
                    >
                      {formatAmount(row)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-24 sm:w-28">
                    <div className="min-w-0">
                      <Badge
                        variant={statusVariant(row)}
                        icon={
                          statusVariant(row) === "success" ? (
                            <FeatherCheckCircle />
                          ) : statusVariant(row) === "warning" ? (
                            <FeatherAlertCircle />
                          ) : undefined
                        }
                      >
                        {truncateText(row.status, 10)}
                      </Badge>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex w-full items-center justify-between mt-4">
              <span className="text-body font-body text-subtext-color">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} results
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  icon={<FeatherChevronLeft />}
                >
                  Previous
                </Button>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  icon={<FeatherChevronRight />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
